/**
 * Application entry point.
 *
 * Wires the router to screens and hands each screen the things it needs. Kept small and without a
 * framework because the shell, the router and the pages are all direct DOM code — a component model
 * would be a fourth thing to keep in step for a page count that is currently two.
 *
 * ## The API origin
 *
 * Read from a `<meta name="nouma-api">` tag rather than hard-coded, because the same bundle is served
 * from three places that need three different answers: `pages.dev` (same origin, via a route rule),
 * a custom domain (same origin again), and local development (a `wrangler dev` on another port). A
 * build-time constant would mean three builds.
 *
 * An empty value means same origin, which is the production case. It is the default deliberately:
 * forgetting to set the meta tag produces a same-origin request, which is correct in production and
 * fails loudly in development, rather than the other way around.
 */

/**
 * The app's screens, loaded on demand.
 *
 * The observation hall is native canvas code and its bundle is ~20 KiB. The Spec Engine screens pull
 * in React and the json-render renderer, which is 700 KiB between them — and inlining that would put
 * a component framework on the critical path of the one screen that never calls it. A dynamic import
 * keeps the front page small and pays for React only where React is used.
 *
 * The cost is a delay on first navigation to a lazy screen, which is why the two screens that are
 * still lazy are both read-only panels rather than the landing experience.
 */

import { SwarmApi } from "./api.ts";
import { Router, matchRoute, type Route } from "./router.ts";
import { createShell, type Shell } from "./shell.ts";
import { applyDirection, detectLang, onLanguageChange, setLang, t } from "../src/i18n/index.ts";
import type { Page } from "./pages/page.ts";

function apiOrigin(): string {
  const meta = document.querySelector('meta[name="nouma-api"]');
  return meta?.getAttribute("content")?.trim() ?? "";
}

/**
 * A fresh API per screen.
 *
 * Not one shared client. Only one screen is mounted at a time, so a per-screen instance still means a
 * single socket — while a shared one would have to be reference-counted, and each page's `dispose`
 * would be closing a connection the next page is about to need.
 */
function makeApi(): SwarmApi {
  return new SwarmApi({ baseUrl: apiOrigin() });
}

/** The mounted screen, so it can be disposed before the next one takes its place. */
let mounted: Page | null = null;

function mount(page: Page): void {
  // Disposed before replacing, not after: the observation hall runs a render loop and holds a socket,
  // and leaving either alive under a new screen would keep drawing into a detached canvas.
  mounted?.dispose();
  mounted = page;
  shell.content.replaceChildren(page.element);
}

/**
 * Mounts a screen that has to be fetched first.
 *
 * The placeholder is mounted immediately so the header stays responsive and the previous screen is
 * torn down — otherwise a slow chunk would leave the old screen's render loop running under a URL
 * that no longer describes it.
 */
function mountLazy(load: () => Promise<Page>, shell: Shell): void {
  const placeholder = document.createElement("section");
  placeholder.className = "page-loading";
  placeholder.textContent = t("shell.loading");
  mount({ element: placeholder, dispose: () => undefined });

  void load()
    .then((page) => {
      // The visitor may have navigated on while the chunk was in flight; mounting then would put the
      // wrong screen under the current URL.
      if (mounted?.element !== placeholder) {
        page.dispose();
        return;
      }
      mount(page);
    })
    .catch((error: unknown) => {
      if (mounted?.element !== placeholder) return;
      placeholder.textContent = error instanceof Error
        ? `This screen could not be loaded: ${error.message}`
        : "This screen could not be loaded.";
      shell.setStatus("screen failed to load", "alarm");
    });
}

const routes: Route[] = [
  {
    path: "#/",
    label: "nav.world",
    description: "nav.world.hint",
    handler: () => {
      // The world is the landing screen: a full-bleed field with the swarm's instruments on it.
      // It reads three endpoints and draws its own canvas, so it is mounted without a framework —
      // the same reasoning as home, which it replaces.
      void import("./pages/world.ts").then(({ createWorldPage }) => {
        mount(createWorldPage(makeApi(), shell));
      });
    },
  },
  {
    path: "#/home",
    // No `label`: the world is the landing screen now, and a second entry beside it would be two
    // names for the front page. `#/home` stays reachable as the plain-text summary.
    handler: () => {
      void import("./pages/home.ts").then(({ createHomePage }) => {
        // Home reads one snapshot and renders no panels, so it needs no framework either.
        mount(createHomePage(makeApi(), shell));
      });
    },
  },
  {
    path: "#/swarm",
    label: "nav.swarm",
    description: "nav.swarm.hint",
    handler: () => {
      void import("./pages/swarm.ts").then(({ createSwarmPage }) => {
        mount(createSwarmPage(makeApi(), shell));
      });
    },
  },
  {
    path: "#/economy",
    label: "nav.economy",
    description: "nav.economy.hint",
    handler: () => {
      mountLazy(
        () =>
          import("./pages/economy.ts").then(({ createEconomyPage }) =>
            createEconomyPage(makeApi(), shell),
          ),
        shell,
      );
    },
  },
  {
    path: "#/map",
    label: "nav.map",
    description: "nav.map.hint",
    handler: () => {
      mountLazy(
        () =>
          import("./pages/map.ts").then(({ createMapPage }) =>
            createMapPage(makeApi(), shell, {
              // The page itself knows nothing about routing; the caller decides what a click means.
              // `#/fly/:id` renders the hall with the drawer open, so a shared link lands on the swarm
              // the fly belongs to rather than on a detached screen.
              onPick: (flyId) => {
                window.location.hash = `#/fly/${flyId}`;
              },
            }),
          ),
        shell,
      );
    },
  },
  {
    path: "#/chronicle",
    label: "nav.chronicle",
    description: "nav.chronicle.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chronicle.ts").then(({ createChroniclePage }) => createChroniclePage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/ledger",
    label: "nav.ledger",
    description: "nav.ledger.hint",
    handler: () => {
      mountLazy(
        () =>
          import("./pages/ledger.ts").then(({ createLedgerPage }) =>
            createLedgerPage(makeApi(), shell),
          ),
        shell,
      );
    },
  },
  {
    path: "#/entrances",
    label: "nav.entrances",
    description: "nav.entrances.hint",
    handler: () => {
      mountLazy(
        () =>
          import("./pages/entrances.ts").then(({ createEntrancesPage }) =>
            createEntrancesPage(makeApi(), shell),
          ),
        shell,
      );
    },
  },
  {
    // The nav entry, with no fly named: the hall plus a drawer on the first fly. Keeping it as its own
    // entry rather than deleting it means the header still describes what a visitor can do here —
    // "inspect one fly" — while the screen underneath stays the swarm.
    path: "#/fly",
    label: "nav.inspector",
    description: "nav.inspector.hint",
    navPath: "#/swarm",
    handler: () => {
      mountLazy(
        () =>
          import("./pages/swarm.ts").then(({ createSwarmPage }) =>
            createSwarmPage(makeApi(), shell, { openFly: 0 }),
          ),
        shell,
      );
    },
  },
  {
    path: "#/breeding",
    label: "nav.breeding",
    description: "nav.breeding.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/breeding.ts").then(({ createBreedingPage }) => createBreedingPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/arena",
    label: "nav.arena",
    description: "nav.arena.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chain-panels.ts").then(({ createArenaPage }) => createArenaPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/governance",
    label: "nav.governance",
    description: "nav.governance.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chain-panels.ts").then(({ createGovernancePage }) => createGovernancePage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/war",
    label: "nav.war",
    description: "nav.war.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chain-panels.ts").then(({ createWarPage }) => createWarPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/land",
    label: "nav.land",
    description: "nav.land.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chain-panels.ts").then(({ createLandPage }) => createLandPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/migration",
    label: "nav.migration",
    description: "nav.migration.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/chain-panels.ts").then(({ createMigrationPage }) => createMigrationPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/docs",
    label: "nav.docs",
    description: "nav.docs.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/docs.ts").then(({ createDocsPage }) => createDocsPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    path: "#/canary",
    label: "nav.canary",
    description: "nav.canary.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/canary.ts").then(({ createCanaryPage }) => createCanaryPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    // Last in the header, and deliberately so: every entry before it answers "what is happening now",
    // and this one answers "what happened". A reader who has not seen the others has no question for
    // it yet.
    path: "#/lab",
    label: "nav.lab",
    description: "nav.lab.hint",
    handler: () => {
      mountLazy(
        () => import("./pages/lab.ts").then(({ createLabPage }) => createLabPage(makeApi(), shell)),
        shell,
      );
    },
  },
  {
    // No label: the header links to `#/swarm`, and this route is reachable by clicking a particle or
    // by URL. It renders the hall and opens the drawer over it, so a shared link lands on the swarm
    // the fly belongs to instead of on a detached page.
    path: "#/fly/:id",
    navPath: "#/swarm",
    handler: (params) => {
      const parsed = Number(params["id"]);
      const openFly = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
      // Spread conditionally rather than passing `{ openFly: undefined }`: `exactOptionalPropertyTypes`
      // distinguishes "no fly named" from "a fly named undefined", and only the first means "no drawer".
      const options = openFly === undefined ? {} : { openFly };
      mountLazy(
        () =>
          import("./pages/swarm.ts").then(({ createSwarmPage }) =>
            createSwarmPage(makeApi(), shell, options),
          ),
        shell,
      );
    },
  },
];

const shell = createShell(routes);

const router = new Router({
  routes,
  fallback: "#/",
  onChange: (match, hash) => {
    shell.setActive(match?.route.navPath ?? match?.route.path ?? "");
    if (match === null) {
      shell.setStatus(`no screen for ${hash}`, "warn");
      return;
    }
    match.route.handler(match.params);
  },
});

/**
 * Re-runs the current route's handler.
 *
 * A language change has to redraw the screen, and the screens build their own text — there is no
 * template to re-scan — so the only honest way to translate a mounted page is to build it again. The
 * hash is read rather than kept in a variable because the router is the thing that owns it; asking
 * `window.location` cannot drift from what is on screen.
 */
function remount(): void {
  const current = matchRoute(routes, window.location.hash);
  if (current !== null) current.route.handler(current.params);
}

/**
 * Starts the application.
 *
 * Awaiting the first dictionary before `router.start()` is deliberate: the alternative is to paint
 * English and swap it a moment later, which flashes the wrong language at exactly the visitor who
 * does not read English. The wait is one chunk, and it only happens for a language other than `en`.
 */
async function boot(): Promise<void> {
  const initial = detectLang();
  if (initial !== "en") {
    try {
      await setLang(initial);
    } catch {
      // A dictionary that failed to fetch leaves English in place. Starting in the wrong language is
      // a worse failure than starting in the fallback, and the picker still works.
      applyDirection();
    }
  } else {
    applyDirection();
  }

  // `createShell` ran before the dictionary was loaded, so the header was built in English. Without
  // this the page would read Arabic and the navigation would still say "Home" — and the listener
  // below only fires on *changes*, so nothing would ever correct it. This line is the difference
  // between "the language loaded" and "the language was applied".
  shell.relabel();

  onLanguageChange(() => {
    shell.relabel();
    remount();
  });

  router.start();
}

void boot();
